import React, { useState } from 'react';
import './AdminNotificationSender.css';

interface AdminNotificationSenderProps {
  className?: string;
}

const AdminNotificationSender: React.FC<AdminNotificationSenderProps> = ({ className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [notification, setNotification] = useState({
    title: '',
    body: '',
    type: 'general' as 'general' | 'reminder' | 'urgent'
  });
  const [isSending, setIsSending] = useState(false);

  const predefinedMessages = {
    headache_reminder: {
      title: 'MalDolor - Recordatorio diario',
      body: '¿Has tenido algún dolor de cabeza hoy? No olvides registrarlo para un mejor seguimiento.',
      type: 'reminder' as const
    },
    tension_reminder: {
      title: 'MalDolor - Control tensión',
      body: 'Es hora de medir tu tensión arterial. ¡Mantén tu salud bajo control!',
      type: 'reminder' as const
    },
    update_reminder: {
      title: 'MalDolor - Mantén tus registros actualizados',
      body: 'Recordatorio: actualiza tus datos de salud para un mejor seguimiento médico.',
      type: 'general' as const
    },
    medication_reminder: {
      title: 'MalDolor - Medicación',
      body: '¿Has tomado tu medicación hoy? No olvides registrarla en la app.',
      type: 'urgent' as const
    }
  };

  const handlePredefinedMessage = (key: keyof typeof predefinedMessages) => {
    const message = predefinedMessages[key];
    setNotification(message);
  };

  const sendNotificationToAllUsers = async () => {
    if (!notification.title || !notification.body) {
      alert('Por favor completa el título y mensaje');
      return;
    }

    setIsSending(true);
    
    try {
      // Simular envío a todos los usuarios conectados
      // En una implementación real, esto sería una llamada API al backend
      
      // Broadcast a través de localStorage para simular push a todos los usuarios activos
      const notificationData = {
        id: Date.now(),
        title: notification.title,
        body: notification.body,
        type: notification.type,
        timestamp: new Date().toISOString(),
        from: 'admin'
      };

      // Guardar en localStorage para que otros tabs/ventanas lo detecten
      localStorage.setItem('admin_notification', JSON.stringify(notificationData));
      
      // Enviar notificación local también (para el admin)
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.body,
          icon: '/logo192.png',
          tag: 'admin-broadcast'
        });
      }

      // Simular delay de envío
      setTimeout(() => {
        setIsSending(false);
        alert(`✅ Notificación enviada a todos los usuarios activos:\n\n"${notification.title}"\n${notification.body}`);
        
        // Limpiar formulario
        setNotification({ title: '', body: '', type: 'general' });
        setIsExpanded(false);
        
        // Limpiar localStorage después de un momento
        setTimeout(() => {
          localStorage.removeItem('admin_notification');
        }, 5000);
      }, 1000);

    } catch (error) {
      console.error('Error enviando notificación:', error);
      alert('❌ Error al enviar la notificación');
      setIsSending(false);
    }
  };

  return (
    <div className={`admin-notification-sender ${className}`}>
      <button
        className="notification-broadcast-btn"
        onClick={() => setIsExpanded(!isExpanded)}
        title="Enviar notificación a todos los usuarios"
      >
        📢
      </button>

      {isExpanded && (
        <div className="notification-sender-panel">
          <div className="sender-header">
            <h4>📢 Enviar notificación masiva</h4>
            <button 
              className="close-btn"
              onClick={() => setIsExpanded(false)}
            >
              ✕
            </button>
          </div>

          <div className="sender-content">
            <div className="predefined-messages">
              <h5>Mensajes predefinidos:</h5>
              <div className="predefined-buttons">
                <button 
                  className="predefined-btn reminder"
                  onClick={() => handlePredefinedMessage('headache_reminder')}
                >
                  🤕 Recordatorio dolor
                </button>
                <button 
                  className="predefined-btn reminder"
                  onClick={() => handlePredefinedMessage('tension_reminder')}
                >
                  💓 Recordatorio tensión
                </button>
                <button 
                  className="predefined-btn general"
                  onClick={() => handlePredefinedMessage('update_reminder')}
                >
                  📝 Actualizar datos
                </button>
                <button 
                  className="predefined-btn urgent"
                  onClick={() => handlePredefinedMessage('medication_reminder')}
                >
                  💊 Medicación
                </button>
              </div>
            </div>

            <div className="custom-message">
              <h5>Mensaje personalizado:</h5>
              
              <div className="form-group">
                <label htmlFor="notification-title">Título:</label>
                <input
                  id="notification-title"
                  type="text"
                  value={notification.title}
                  onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ej: MalDolor - Recordatorio importante"
                  maxLength={50}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notification-body">Mensaje:</label>
                <textarea
                  id="notification-body"
                  value={notification.body}
                  onChange={(e) => setNotification(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Escribe tu mensaje aquí..."
                  maxLength={150}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notification-type">Tipo:</label>
                <select
                  id="notification-type"
                  value={notification.type}
                  onChange={(e) => setNotification(prev => ({ ...prev, type: e.target.value as any }))}
                >
                  <option value="general">General</option>
                  <option value="reminder">Recordatorio</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
            </div>

            <div className="sender-actions">
              <button
                className="send-btn"
                onClick={sendNotificationToAllUsers}
                disabled={isSending || !notification.title || !notification.body}
              >
                {isSending ? '📤 Enviando...' : '📢 Enviar a todos'}
              </button>
            </div>

            <div className="sender-info">
              <small>
                ⚠️ Esta notificación se enviará a todos los usuarios que tengan la aplicación abierta y permisos activados.
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationSender;